import { BarChart3, Pencil, Plus, Radio } from "lucide-react";
import {
  useCreate,
  useDelete,
  useLocaleState,
  useNotify,
  useTranslate,
  useUpdate,
} from "ra-core";
import {
  useEffect,
  useId,
  useState,
  type CSSProperties,
  type FormEvent,
} from "react";

import { PlatformBadge } from "@/components/competitors/PlatformIcon";
import {
  PLATFORMS,
  platformLabelKey,
} from "@/components/competitors/platforms";
import type { SocialPlatform } from "@/components/competitors/types";
import {
  ConfirmDeleteButton,
  FormActions,
  IconAction,
} from "@/components/competitors/ui";
import {
  EmptyState,
  ExternalAnchor,
  LoadingCards,
} from "@/components/market-intel/components";
import {
  Field,
  FieldError,
  FormDialog,
  SELECT_CLASS,
} from "@/components/market-intel/FormDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useMoneyFormat } from "@/hooks/use-money-format";
import { useIsMobile } from "@/hooks/use-mobile";

import { CHANNEL_STATUSES, channelStatusLabelKey } from "./constants";
import { useMarketing } from "./context";
import {
  channelFormDefaults,
  normalizeChannelForm,
  type ChannelFormValues,
} from "./forms";
import type { ChannelStatus, MarketingChannel } from "./types";

/** Marketing > Canales: Litus' own accounts and the blog. */
export const ChannelsView = ({ isPending }: { isPending: boolean }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const { formatMoney } = useMoneyFormat();
  const { data, recordMetrics } = useMarketing();
  const [update] = useUpdate<MarketingChannel>();
  const [editing, setEditing] = useState<MarketingChannel | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  const openForm = (channel: MarketingChannel | null) => {
    setEditing(channel);
    setFormOpen(true);
  };
  const toggle = (channel: MarketingChannel, active: boolean) => {
    const status: ChannelStatus = active ? "activo" : "pausado";
    update(
      "marketing_channels",
      { id: channel.id, data: { status }, previousData: channel },
      {
        mutationMode: "optimistic",
        onSuccess: () =>
          notify("marketing.channels.status_updated", {
            type: "success",
            messageArgs: {
              status: translate(channelStatusLabelKey(status)).toLowerCase(),
            },
          }),
        onError: () => notify("marketing.form.error", { type: "error" }),
      },
    );
  };
  const newLabel = translate("marketing.channels.new");

  if (isPending) return <LoadingCards />;

  return (
    <section className="space-y-3 md:space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-semibold tracking-tight">
          {translate("marketing.channels.heading")}
        </h2>
        <Button
          type="button"
          className="btn-save h-9 rounded-full px-4"
          onClick={() => openForm(null)}
        >
          <Plus aria-hidden className="size-4" />
          {newLabel}
        </Button>
      </div>
      {!data.channels.length ? (
        <EmptyState
          icon={Radio}
          title={translate("marketing.channels.empty")}
          hint={translate("marketing.channels.empty_hint")}
        />
      ) : (
        <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4 xl:grid-cols-3">
          {data.channels.map((channel, index) => {
            const pieces = data.items.filter(
              (item) => String(item.channel_id) === String(channel.id),
            ).length;
            const active = channel.status === "activo";
            const switchId = `mk-channel-${channel.id}-status`;
            return (
              <li
                key={channel.id}
                className="mi-card mi-rise flex min-w-0 flex-col gap-3 p-4"
                data-paused={active ? undefined : "true"}
                style={{ "--mi-index": index } as CSSProperties}
              >
                <div className="flex items-start gap-3">
                  <PlatformBadge platform={channel.platform} />
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate text-[17px] leading-tight font-semibold">
                      {channel.name}
                    </h3>
                    {channel.url ? (
                      <ExternalAnchor href={channel.url} className="text-sm">
                        {channel.handle || channel.url}
                      </ExternalAnchor>
                    ) : channel.handle ? (
                      <p className="truncate text-sm text-muted-foreground">
                        {channel.handle}
                      </p>
                    ) : null}
                  </div>
                  <IconAction
                    label={translate("marketing.channels.edit")}
                    icon={Pencil}
                    onClick={() => openForm(channel)}
                  />
                </div>
                {channel.objective ? (
                  <p className="text-sm text-muted-foreground">
                    {channel.objective}
                  </p>
                ) : null}
                <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-border/60 pt-3 text-sm">
                  <label
                    htmlFor={switchId}
                    className="inline-flex items-center gap-2 font-medium"
                  >
                    <Switch
                      id={switchId}
                      checked={active}
                      onCheckedChange={(checked) => toggle(channel, checked)}
                      aria-label={translate(
                        active
                          ? "marketing.channels.pause"
                          : "marketing.channels.activate",
                      )}
                    />
                    {translate(channelStatusLabelKey(channel.status))}
                  </label>
                  <span className="text-muted-foreground">
                    <span className="sr-only">
                      {translate("marketing.channels.budget")}:{" "}
                    </span>
                    {channel.monthly_budget != null
                      ? translate("marketing.channels.per_month", {
                          amount: formatMoney(channel.monthly_budget),
                        })
                      : translate("marketing.channels.no_budget")}
                  </span>
                  <span className="text-muted-foreground">
                    {translate("marketing.channels.pieces", {
                      smart_count: pieces,
                    })}
                  </span>
                  <button
                    type="button"
                    className="mk-link ml-auto"
                    onClick={() =>
                      recordMetrics({ kind: "channel", id: channel.id })
                    }
                  >
                    <BarChart3 aria-hidden className="size-4" />
                    {translate("marketing.analytics.record")}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <ChannelForm
        open={formOpen}
        onOpenChange={setFormOpen}
        channel={editing}
      />
    </section>
  );
};

export const ChannelForm = ({
  open,
  onOpenChange,
  channel,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  channel: MarketingChannel | null;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [locale = "es"] = useLocaleState();
  const isMobile = useIsMobile();
  const formId = useId();
  const [values, setValues] = useState(() =>
    channelFormDefaults(channel, locale),
  );
  const [error, setError] = useState<string | null>(null);
  const [create, { isPending: creating }] = useCreate();
  const [update, { isPending: updating }] = useUpdate();
  const [remove, { isPending: deleting }] = useDelete();

  useEffect(() => {
    if (open) {
      setValues(channelFormDefaults(channel, locale));
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, channel]);

  const set =
    <K extends keyof ChannelFormValues>(key: K) =>
    (value: ChannelFormValues[K]) => {
      setValues((current) => ({ ...current, [key]: value }));
      setError(null);
    };
  const close = () => onOpenChange(false);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const result = normalizeChannelForm(values);
    if (!result.ok) return setError(result.error);
    const options = {
      onSuccess: () => {
        notify("marketing.form.saved", { type: "success" });
        close();
      },
      onError: () => notify("marketing.form.error", { type: "error" }),
    };
    if (channel)
      update(
        "marketing_channels",
        { id: channel.id, data: result.data, previousData: channel },
        options,
      );
    else create("marketing_channels", { data: result.data }, options);
  };
  const id = (name: string) => `${formId}-${name}`;
  const errorId = id("error");

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={translate(
        channel ? "marketing.channels.edit" : "marketing.channels.new",
      )}
      formId={formId}
      onSubmit={submit}
      actions={
        <>
          {channel ? (
            <ConfirmDeleteButton
              label={translate("marketing.channels.delete")}
              disabled={deleting}
              className="mr-auto"
              onConfirm={() =>
                remove(
                  "marketing_channels",
                  { id: channel.id, previousData: channel },
                  {
                    onSuccess: () => {
                      notify("marketing.channels.deleted", { type: "info" });
                      close();
                    },
                  },
                )
              }
            />
          ) : null}
          <FormActions
            formId={formId}
            disabled={creating || updating}
            onCancel={close}
          />
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <Field
          id={id("platform")}
          label={translate("marketing.channels.form.platform")}
        >
          <select
            id={id("platform")}
            className={SELECT_CLASS}
            value={values.platform}
            onChange={(event) =>
              set("platform")(event.target.value as SocialPlatform)
            }
          >
            {PLATFORMS.map((platform) => (
              <option key={platform} value={platform}>
                {translate(platformLabelKey(platform))}
              </option>
            ))}
          </select>
        </Field>
        <Field
          id={id("status")}
          label={translate("marketing.channel_status.label")}
        >
          <select
            id={id("status")}
            className={SELECT_CLASS}
            value={values.status}
            onChange={(event) =>
              set("status")(event.target.value as ChannelStatus)
            }
          >
            {CHANNEL_STATUSES.map((status) => (
              <option key={status} value={status}>
                {translate(channelStatusLabelKey(status))}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field id={id("name")} label={translate("marketing.channels.form.name")}>
        <Input
          id={id("name")}
          value={values.name}
          onChange={(event) => set("name")(event.target.value)}
          aria-invalid={error === "marketing.form.name_required" || undefined}
          aria-describedby={error ? errorId : undefined}
          autoFocus={!isMobile && !channel}
          maxLength={120}
        />
      </Field>
      {error ? <FieldError id={errorId}>{translate(error)}</FieldError> : null}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          id={id("handle")}
          label={translate("marketing.channels.form.handle")}
        >
          <Input
            id={id("handle")}
            value={values.handle}
            onChange={(event) => set("handle")(event.target.value)}
          />
        </Field>
        <Field id={id("url")} label={translate("marketing.channels.form.url")}>
          <Input
            id={id("url")}
            type="url"
            inputMode="url"
            value={values.url}
            onChange={(event) => set("url")(event.target.value)}
          />
        </Field>
      </div>
      <Field
        id={id("objective")}
        label={translate("marketing.channels.form.objective")}
      >
        <Input
          id={id("objective")}
          value={values.objective}
          onChange={(event) => set("objective")(event.target.value)}
        />
      </Field>
      <Field
        id={id("budget")}
        label={translate("marketing.channels.form.monthly_budget")}
      >
        <Input
          id={id("budget")}
          inputMode="decimal"
          value={values.monthly_budget}
          onChange={(event) => set("monthly_budget")(event.target.value)}
          aria-invalid={error === "marketing.form.amount_invalid" || undefined}
        />
      </Field>
      <Field
        id={id("notes")}
        label={translate("marketing.channels.form.notes")}
      >
        <Textarea
          id={id("notes")}
          rows={3}
          value={values.notes}
          onChange={(event) => set("notes")(event.target.value)}
        />
      </Field>
    </FormDialog>
  );
};
